import React, { useState } from 'react';
import styles from './VideoStyles.module.css';
import Alert from '@mui/material/Alert';
import apiClient from '../../api/apiClient';
import MainLayout from '../../layouts/MainLayout';
import { ProgressBar, Spinner } from '../../components/Progress';
import checkStatus from '../../hooks/checkAnalysisStatus';
import runProgressStep from '../../hooks/runProgressStep';
import SearchableStartupDropdown from '../../components/SearchableStartupDropdown';
import ToggleSwitch from '../../components/ToggleSwitch';

const client = apiClient('transcription');
const client_sentiment = apiClient('sentiment');
const client_emotions = apiClient('emotions');

function UploadAnalysisPage() {
  const [file, setFile] = useState(null);
  const [startupName, setStartupName] = useState('');
  const [sentimentCoding, setSentimentCoding] = useState(false);
  const [llmAnalysis, setLlmAnalysis] = useState(false);
  const [response, setResponse] = useState('');
  const [errors, setError] = useState('');

  const [sentimentCodingRunning, setSentimentCodingRunning] = useState(false);
  const [sentimentCodingProgress, setsentimentCodingProgress] = useState(0);

  const [llmAnalysisRunning, setLLMAnalysisRunning] = useState(false);
  const [llmAnalysisProgress, setLLMAnalysisProgress] = useState(0);

  const [uploadType, setUploadType] = useState(''); // 'transcript' or 'emotions' or 'coding'
  const [emotionsUploadMode, setEmotionsUploadMode] = useState('zip');
  const [emotionsFiles, setEmotionsFiles] = useState({
    respondent_baseline1: null,
    respondent_EOD: null,
    respondent_baseline2: null,
    respondent_discussion: null,
    respondent_baseline3: null,
    environment_baseline1: null,
    environment_EOD: null,
    environment_baseline2: null,
    environment_discussion: null,
    environment_baseline3: null,
  });
  const [emotionsZipFile, setEmotionsZipFile] = useState(null);

  const [sentimentCodingFile, setSentimentCodingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    if (!file) {
      alert('Please select a .docx file to upload.');
      setUploading(false);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('startup_name', startupName);
    formData.append('sentiment_coding', sentimentCoding);
    formData.append('llm_analysis', llmAnalysis);

    try {
      const res = await client.post('/upload/transcript', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res.status === 200) {
        if (llmAnalysis) {
          startLLMAnalysisStep();
        } else if (sentimentCoding) {
          startSentimentCodingStep();
        } else {
          setResponse(
            'File uploaded successfully! No further analysis triggered.'
          );
        }
      }
      setUploading(false);
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Upload failed. Please try again. ' + error.message);
      setUploading(false);
    }
  };

  const handleEmotionsSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    const formData = new FormData();
    formData.append('startup_name', startupName);

    if (emotionsUploadMode === 'zip') {
      if (!emotionsZipFile) {
        alert('Please choose a ZIP file first');
        setUploading(false);
        return;
      }
      formData.append('bundle', emotionsZipFile);
    } else {
      // multi‑file validation
      for (const [key, file] of Object.entries(emotionsFiles)) {
        if (!file) {
          alert(`Please upload file for: ${key}`);
          setUploading(false);
          return;
        }
        formData.append(key, file);
      }
    }

    try {
      const res = await client_emotions.post('/upload/emotions', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status === 200)
        setResponse('Emotions data successfully uploaded!');
      else setError('Upload failed.');
    } catch (err) {
      console.error('Error while uploading:', err);
      setError('Upload failed. ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSentimentCodingSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    if (!sentimentCodingFile) {
      alert('Please select a CSV file for sentiment coding.');
      setUploading(false);
      return;
    }
    const formData = new FormData();
    formData.append('file', sentimentCodingFile);
    formData.append('startup_name', startupName);

    try {
      const res = await client_sentiment.post('/upload/coding', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res.status === 200) {
        setResponse('Sentiment-Coding Data successfully uploaded!');
      } else {
        setError('Upload failed.');
      }
      setUploading(false);
    } catch (error) {
      console.error('Error while uploading the csv:', error);
      setError('Upload failed. ' + error.message);
      setUploading(false);
    }
  };

  const handleEmotionsFileChange = (e, key) => {
    const file = e.target.files?.[0] ?? null;
    if (emotionsUploadMode === 'multi' && key) {
      setEmotionsFiles((prev) => ({ ...prev, [key]: file }));
    } else if (emotionsUploadMode === 'zip') {
      setEmotionsZipFile(file);
    }
  };

  const startSentimentCodingStep = () => {
    runProgressStep({
      setRunning: setSentimentCodingRunning,
      setProgress: setsentimentCodingProgress,
      fallbackDurationMs: 30 * 1000, // 30s fallback
      estimatedDurationSeconds: 30, // max 30s for sentiment coding
      onComplete: checkSentimentCodingStatus,
      scale: 1.0,
    });
  };

  const startLLMAnalysisStep = () => {
    runProgressStep({
      setRunning: setLLMAnalysisRunning,
      setProgress: setLLMAnalysisProgress,
      fallbackDurationMs: 10 * 60 * 1000,
      estimatedDurationSeconds: 10 * 60,
      onComplete: checkLLMAnalysisStatus,
      scale: 1.0,
    });
  };

  const checkSentimentCodingStatus = () => {
    checkStatus({
      client: client_sentiment,
      endpoint: `/status/coding/${startupName}`,
      setRunning: setSentimentCodingRunning,
      setError,
      onSuccess: () => setResponse('Analysis completed successfully!'),
      nextStep: llmAnalysis ? startLLMAnalysisStep : null,
      pollingLabel: 'Sentiment Coding',
    });
  };

  const checkLLMAnalysisStatus = () => {
    checkStatus({
      client: client_sentiment,
      endpoint: `/status/llm/${startupName}`,
      setRunning: setLLMAnalysisRunning,
      setError,
      onSuccess: () => setResponse('Analysis should now be completed!'),
      pollingLabel: 'LLM Analysis',
    });
  };

  return (
    <MainLayout>
      <div className={styles.uploadContainer}>
        <h1>Upload Analysis</h1>
        <div className={styles.filterBox}>
          <div className={styles.filterRow2col}>
            <div className={styles.filterCol}>
              <label className={styles.filterLabelAbove}>Upload-Type:</label>
              <select
                className={styles.input}
                value={uploadType}
                onChange={(e) => {
                  setUploadType(e.target.value);
                  setResponse('');
                  setError('');
                }}
              >
                <option value="">Select a Type</option>
                <option value="transcript">Transcript</option>
                <option value="emotions">Emotions Analysis</option>
                <option value="coding">Coding Analysis</option>
              </select>
            </div>
            <div className={styles.filterCol}>
              <label className={styles.filterLabelAbove}>Startup Name:</label>
              <SearchableStartupDropdown
                selectedStartup={startupName}
                setSelectedStartup={setStartupName}
              />
            </div>
          </div>
          {uploadType === 'transcript' && (
            <div className={styles.checkboxRow}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={sentimentCoding}
                  onChange={(e) => setSentimentCoding(e.target.checked)}
                />
                Trigger Sentiment Coding
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={llmAnalysis}
                  onChange={(e) => setLlmAnalysis(e.target.checked)}
                />
                Trigger LLM Analysis
              </label>
            </div>
          )}
          {uploadType === 'emotions' && (
            <ToggleSwitch
              label="ZIP upload"
              checked={emotionsUploadMode === 'zip'}
              onChange={(e) =>
                setEmotionsUploadMode(e.target.checked ? 'zip' : 'multi')
              }
            />
          )}
        </div>
        {uploadType === 'transcript' && (
          <form onSubmit={handleSubmit} className={styles.uploadForm}>
            <div className={styles.uploadSections}>
              <div className={`${styles.uploadBox} ${styles.uploadBoxNarrow}`}>
                <h2>Transcript File</h2>
                <div className={styles.uploadRow}>
                  <label>DOCX File:</label>
                  <input
                    type="file"
                    accept=".docx"
                    onChange={(e) => setFile(e.target.files[0])}
                    required
                  />
                </div>
              </div>
            </div>
            <button type="submit" disabled={uploading}>
              Upload
            </button>
          </form>
        )}

        {uploadType === 'emotions' && emotionsUploadMode === 'zip' && (
          <form onSubmit={handleEmotionsSubmit} className={styles.uploadForm}>
            <div className={styles.uploadSections}>
              <div className={`${styles.uploadBox} ${styles.uploadBoxNarrow}`}>
                <h2>Select ZIP file (all CSVs bundled)</h2>
                <div className={styles.uploadRow}>
                  <label>ZIP File:</label>
                  <input
                    type="file"
                    accept=".zip"
                    required
                    onChange={(e) => handleEmotionsFileChange(e)}
                  />
                </div>
              </div>
            </div>
            <button type="submit" disabled={uploading}>
              {uploading ? 'Uploading…' : 'Upload ZIP'}
            </button>
          </form>
        )}

        {uploadType === 'emotions' && emotionsUploadMode === 'multi' && (
          <form
            onSubmit={handleEmotionsSubmit}
            className={styles.uploadFormResponsive}
          >
            <div className={styles.uploadSections}>
              <div className={styles.uploadBox}>
                <h2>Respondent Files</h2>
                {Object.keys(emotionsFiles)
                  .filter((key) => key.startsWith('respondent'))
                  .map((key) => (
                    <div key={key} className={styles.uploadRow}>
                      <label>{key.replace(/_/g, ' ')}:</label>
                      <input
                        type="file"
                        accept=".csv"
                        required
                        onChange={(e) => handleEmotionsFileChange(e, key)}
                      />
                    </div>
                  ))}
              </div>
              <div className={styles.uploadBox}>
                <h2>Environment Files</h2>
                {Object.keys(emotionsFiles)
                  .filter((key) => key.startsWith('environment'))
                  .map((key) => (
                    <div key={key} className={styles.uploadRow}>
                      <label>{key.replace(/_/g, ' ')}:</label>
                      <input
                        type="file"
                        accept=".csv"
                        required
                        onChange={(e) => handleEmotionsFileChange(e, key)}
                      />
                    </div>
                  ))}
              </div>
            </div>
            <button type="submit" disabled={uploading}>
              Upload
            </button>
          </form>
        )}

        {uploadType === 'coding' && (
          <form
            onSubmit={handleSentimentCodingSubmit}
            className={styles.uploadForm}
          >
            <div className={styles.uploadSections}>
              <div className={styles.uploadBox}>
                <h2>Sentiment Coding File</h2>
                <div className={styles.uploadRow}>
                  <label>CSV File:</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setSentimentCodingFile(e.target.files[0])}
                    required
                  />
                </div>
              </div>
            </div>
            <button type="submit" disabled={uploading}>
              Upload
            </button>
          </form>
        )}

        {uploadType === 'transcript' && sentimentCodingRunning && (
          <div>
            <p>
              Sentiment Coding in progress...
              {' approx. 1 min'}
            </p>
            <ProgressBar percentage={sentimentCodingProgress} />
          </div>
        )}

        {uploadType === 'transcript' && llmAnalysisRunning && (
          <div>
            {sentimentCoding && <p>✅ Sentiment Coding</p>}
            <p>
              LLM Analysis in progress...
              {' approx. 10 min'}
            </p>
            <ProgressBar percentage={llmAnalysisProgress} />
          </div>
        )}

        {(response || errors) && (
          <div>
            {response && <Alert severity="success">{response}</Alert>}
            {errors && <Alert severity="error">{errors}</Alert>}
          </div>
        )}

        {uploading && (
          <p>
            Uploading... <Spinner />
          </p>
        )}
      </div>
    </MainLayout>
  );
}

export default UploadAnalysisPage;
